import { atticApiKeyUrl } from "@dashboard/atticApiKeys/urls";
import { formatCost, formatLatency, RequestStrip, UsageTotal } from "@dashboard/atticShared/usage";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { ExtensionsUrls } from "@dashboard/extensions/urls";
import { sectionNames } from "@dashboard/intl";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { type IntlShape, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { callerMessages, columnMessages, rangeMessages, sectionMessages } from "../../messages";
import {
  type AtticApiUsageAppName,
  type AtticApiUsageAppRow,
  type AtticApiUsageCallerRow,
  type AtticApiUsageRange,
  type AtticApiUsageTotals,
  RANGE_DAYS,
} from "../../utils";

const formatShare = (intl: IntlShape, share: number) =>
  intl.formatNumber(share, { style: "percent", maximumFractionDigits: 1 });

const AppName = ({ name }: { name: AtticApiUsageAppName }) => {
  const intl = useIntl();

  if (name.kind === "loading") {
    return (
      <Box __width="120px" data-test-id="attic-api-usage-app-name-loading">
        <Skeleton />
      </Box>
    );
  }

  if (name.kind === "apiKey") {
    return (
      <Box display="flex" gap={2} alignItems="baseline">
        <Link to={atticApiKeyUrl(name.keyId)}>
          <Text size={3}>{name.label}</Text>
        </Link>
        <Text size={2} color="default2">
          {intl.formatMessage(columnMessages.apiKey)}
        </Text>
      </Box>
    );
  }

  if (name.kind === "app") {
    return (
      <Link to={ExtensionsUrls.resolveViewManifestExtensionUrl(name.appId)}>
        <Text size={3}>{name.label}</Text>
      </Link>
    );
  }

  return (
    <Text size={3}>{intl.formatMessage(columnMessages.unknownApp, { number: name.number })}</Text>
  );
};

export interface AtticApiUsagePageData {
  totals: AtticApiUsageTotals;
  callers: AtticApiUsageCallerRow[];
  apps: Array<AtticApiUsageAppRow & { name: AtticApiUsageAppName }>;
  daily: Array<{ day: string; requests: number }>;
}

export interface AtticApiUsagePageProps {
  range: AtticApiUsageRange;
  /** Undefined while the first read is in flight. */
  data: AtticApiUsagePageData | undefined;
  loadFailed: boolean;
  onRangeChange: (range: AtticApiUsageRange) => void;
}

export const AtticApiUsagePage = ({
  range,
  data,
  loadFailed,
  onRangeChange,
}: AtticApiUsagePageProps) => {
  const intl = useIntl();
  const empty = !!data && data.totals.requests === 0;

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionNames.atticApiUsage)}>
        <Box display="flex" gap={2}>
          {RANGE_DAYS.map(days => (
            <Button
              key={days}
              variant={days === range ? "primary" : "secondary"}
              onClick={() => onRangeChange(days)}
              data-test-id={`attic-api-usage-range-${days}`}
            >
              {intl.formatMessage(rangeMessages.days, { days })}
            </Button>
          ))}
        </Box>
      </TopNav>
      <Box display="flex" flexDirection="column" gap={4} paddingBottom={6}>
        <DashboardCard data-test-id="attic-api-usage-summary">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(columnMessages.summary)}</DashboardCard.Title>
            <DashboardCard.Subtitle data-test-id="attic-api-usage-freshness">
              {intl.formatMessage(sectionMessages.freshness)}
            </DashboardCard.Subtitle>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {loadFailed ? (
              <Text size={3} color="critical1" data-test-id="attic-api-usage-load-failed">
                {intl.formatMessage(sectionMessages.loadFailed)}
              </Text>
            ) : !data ? (
              <Box
                display="flex"
                flexDirection="column"
                gap={3}
                data-test-id="attic-api-usage-loading"
              >
                <Skeleton />
                <Skeleton />
              </Box>
            ) : (
              <>
                <Box display="grid" gridTemplateColumns={{ mobile: 2, desktop: 6 }} gap={5}>
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.requests)}
                    value={intl.formatNumber(data.totals.requests)}
                    testId="attic-api-usage-requests"
                  />
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.errors)}
                    value={intl.formatNumber(data.totals.errors)}
                    testId="attic-api-usage-errors"
                  />
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.throttled)}
                    value={intl.formatNumber(data.totals.throttled)}
                    testId="attic-api-usage-throttled"
                  />
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.cost)}
                    value={formatCost(intl, data.totals.cost)}
                    testId="attic-api-usage-cost"
                  />
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.writes)}
                    value={intl.formatNumber(data.totals.writes)}
                    testId="attic-api-usage-writes"
                  />
                  <UsageTotal
                    label={intl.formatMessage(columnMessages.latency)}
                    value={formatLatency(intl, data.totals.meanLatencyMs)}
                    testId="attic-api-usage-latency"
                  />
                </Box>
                {empty ? (
                  <Text
                    as="p"
                    size={3}
                    color="default2"
                    marginTop={5}
                    data-test-id="attic-api-usage-empty"
                  >
                    {intl.formatMessage(sectionMessages.empty)}
                  </Text>
                ) : (
                  <Box marginTop={5}>
                    <RequestStrip rows={data.daily} testId="attic-api-usage-strip" />
                  </Box>
                )}
              </>
            )}
          </DashboardCard.Content>
        </DashboardCard>

        {data && !empty && (
          <DashboardCard data-test-id="attic-api-usage-callers">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(columnMessages.byCaller)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(columnMessages.caller)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.requests)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.share)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.errors)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.cost)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.writes)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(columnMessages.latency)}</TableCell>
                </TableRowLink>
              </TableHead>
              <TableBody>
                {data.callers.map(row => (
                  <TableRowLink
                    key={row.caller}
                    data-test-id={`attic-api-usage-caller-${row.caller}`}
                  >
                    <TableCell>{intl.formatMessage(callerMessages[row.caller])}</TableCell>
                    <TableCell align="right">{intl.formatNumber(row.requests)}</TableCell>
                    <TableCell align="right">{formatShare(intl, row.share)}</TableCell>
                    <TableCell align="right">{intl.formatNumber(row.errors)}</TableCell>
                    <TableCell align="right">{formatCost(intl, row.cost)}</TableCell>
                    <TableCell align="right">{intl.formatNumber(row.writes)}</TableCell>
                    <TableCell align="right">{formatLatency(intl, row.meanLatencyMs)}</TableCell>
                  </TableRowLink>
                ))}
              </TableBody>
            </ResponsiveTable>
          </DashboardCard>
        )}

        {data && !empty && (
          <DashboardCard data-test-id="attic-api-usage-apps">
            <DashboardCard.Header>
              <DashboardCard.Title>{intl.formatMessage(columnMessages.byApp)}</DashboardCard.Title>
              <DashboardCard.Subtitle>
                {intl.formatMessage(columnMessages.byAppDescription)}
              </DashboardCard.Subtitle>
            </DashboardCard.Header>
            {data.apps.length === 0 ? (
              <DashboardCard.Content>
                <Text size={3} color="default2" data-test-id="attic-api-usage-no-apps">
                  {intl.formatMessage(columnMessages.noApps)}
                </Text>
              </DashboardCard.Content>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRowLink>
                    <TableCell>{intl.formatMessage(columnMessages.name)}</TableCell>
                    <TableCell align="right">
                      {intl.formatMessage(columnMessages.requests)}
                    </TableCell>
                    <TableCell align="right">{intl.formatMessage(columnMessages.errors)}</TableCell>
                    <TableCell align="right">{intl.formatMessage(columnMessages.cost)}</TableCell>
                    <TableCell align="right">{intl.formatMessage(columnMessages.writes)}</TableCell>
                    <TableCell>{intl.formatMessage(columnMessages.lastSeen)}</TableCell>
                  </TableRowLink>
                </TableHead>
                <TableBody>
                  {data.apps.map(row => (
                    <TableRowLink key={row.appId} data-test-id="attic-api-usage-app">
                      <TableCell>
                        <AppName name={row.name} />
                      </TableCell>
                      <TableCell align="right">{intl.formatNumber(row.requests)}</TableCell>
                      <TableCell align="right">{intl.formatNumber(row.errors)}</TableCell>
                      <TableCell align="right">{formatCost(intl, row.cost)}</TableCell>
                      <TableCell align="right">{intl.formatNumber(row.writes)}</TableCell>
                      <TableCell>
                        {row.lastDay ? <DayLabel date={row.lastDay} plain /> : "-"}
                      </TableCell>
                    </TableRowLink>
                  ))}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}
      </Box>
    </ListPageLayout>
  );
};

export default AtticApiUsagePage;
